import { CreateInDialog } from "@/components/common/CreateInDialog";
import { ResourceName, Policy, Vendor, ValidAny, VendorCreate } from "@/types";
import { getProps } from "@/utils/helpers/columnsHelper";
import { useDefineAppLocation } from "@react-admin/ra-navigation";
import { useState, useMemo } from "react";
import {
  TextField,
  CreateButton,
  TopToolbar,
  FunctionField,
  ListContextProvider,
  useListController,
  SelectColumnsButton,
  ExportButton,
  useGetList,
} from "react-admin";
import { ChunkedExportButton } from "@/components/common/ChunkedExportButton";
import { useGetListAll } from "@/providers/dataProvider/useGetListAll";
import { EXPORT_MAX_RECORDS } from "@/providers/dataProvider/paginationLimits";
import { Box, Typography, Tooltip } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { VendorsListAside } from "./components/VendorsListAside";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { PageHeader } from "@/components/common/PageHeader";
import { getResourceIcon } from "@/utils/icons";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { StatusChipColor, StatusChip } from "@/components/common/fields/StatusChipField";
import { ColorType } from "@/types/shared/uiComponents";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { isNonProductionEnvironment } from "@/utils/environment";
import { jsonExporter } from "@/utils/exporters/jsonExporter";
import { Download as DownloadIcon } from "@mui/icons-material";

/**
 * Strips empty strings, null, and undefined from vendor payload (including nested objects).
 * Prevents backend 400 errors caused by empty nested contact/address objects.
 */
export const transformVendorCreate = (data: ValidAny): ValidAny => {
  const cleaned: Record<string, ValidAny> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value === "") continue;
    if (typeof value === "object" && !Array.isArray(value)) {
      const nested = transformVendorCreate(value);
      // Only include nested objects if they have at least one non-empty value
      if (Object.keys(nested).length > 0) {
        cleaned[key] = nested;
      }
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

const getVendorTitle = (customResource?: string): string => {
  switch (customResource) {
    case "vendors/carriers":
      return "Carriers";
    case "vendors/tpas":
      return "TPAs";
    case "vendors/rx":
      return "Rx Vendors";
    default:
      return "Vendors";
  }
};

/** Shows a count of active policies associated with this vendor, with client names on hover */
const ActivePoliciesCell = ({
  vendor,
  policyCountMap,
  policyClientMap,
}: {
  vendor: Vendor;
  policyCountMap: Record<string, number>;
  policyClientMap: Record<string, string[]>;
}) => {
  const count = policyCountMap[vendor.id] || 0;
  const clientNames = policyClientMap[vendor.id] || [];

  if (count === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  const tooltipContent = clientNames.length > 0 ? clientNames.join("\n") : `${count} active policies`;

  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: "pre-line", fontSize: "0.75rem" }}>{tooltipContent}</Box>}
      arrow
      placement="top"
    >
      <Typography
        variant="body2"
        component={RouterLink}
        to={(() => {
          // Build filter based on vendor type — carrier uses vendor_id_ref, TPA/Rx use their array filters
          // If vendor is multiple types, prefer vendor_id_ref (carrier) as the broadest match
          const filter: Record<string, string> = {};
          if (vendor.is_sl_carrier) {
            filter.vendor_id_ref = vendor.id;
          } else if (vendor.is_tpa) {
            filter.tpa_vendor_id = vendor.id;
          } else if (vendor.is_rx_vendor) {
            filter.rx_vendor_id = vendor.id;
          } else {
            filter.vendor_id_ref = vendor.id;
          }
          return `/policies?filter=${encodeURIComponent(JSON.stringify(filter))}`;
        })()}
        sx={{
          color: "primary.main",
          textDecoration: "underline",
          fontWeight: 500,
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {count}
      </Typography>
    </Tooltip>
  );
};

export const VendorsList = ({ customResource }: { customResource?: string }) => {
  // Explicit default sort preserves the prior server order (company name A-Z)
  // now that the API honors sort. Without it react-admin would default to id ASC.
  const listContext = useListController({
    resource: ResourceName.VENDORS,
    sort: { field: "company_name", order: "ASC" },
  });
  const { isPending: loading } = listContext;
  const VendorsIcon = getResourceIcon(ResourceName.VENDORS);

  // Fetch active policies for the policy count column
  const { data: activePolicies = [] } = useGetList<Policy>(ResourceName.POLICIES, {
    filter: { status: "active" },
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "id", order: "ASC" },
  });

  // Fetch clients for tooltip names. Uses useGetListAll (chunked) rather than a
  // single perPage:10000 request — the client-list endpoint is page-size capped
  // (#5166), so a single big page would silently return only the first 1000 and
  // degrade tooltips to raw UUIDs for partners with >1000 clients.
  const { data: clients } = useGetListAll(ResourceName.CLIENTS, {
    sort: { field: "client_legal_name", order: "ASC" },
    maxRecords: EXPORT_MAX_RECORDS,
  });

  const clientNameMap = useMemo(
    () => clients.reduce<Record<string, string>>((acc, c) => ({ ...acc, [c.id]: c.client_legal_name || c.id }), {}),
    [clients]
  );

  // Build policy count and client names per vendor
  const { policyCountMap, policyClientMap } = useMemo(() => {
    const countMap: Record<string, number> = {};
    const clientMap: Record<string, Set<string>> = {};

    for (const policy of activePolicies) {
      // vendor_id_ref (stop-loss carrier)
      if (policy.vendor_id_ref) {
        countMap[policy.vendor_id_ref] = (countMap[policy.vendor_id_ref] || 0) + 1;
        if (!clientMap[policy.vendor_id_ref]) clientMap[policy.vendor_id_ref] = new Set();
        if (policy.client_id_ref && clientNameMap[policy.client_id_ref]) {
          clientMap[policy.vendor_id_ref].add(clientNameMap[policy.client_id_ref]);
        }
      }
      // tpa_vendor_ids
      if (policy.tpa_vendor_ids) {
        for (const tpaId of policy.tpa_vendor_ids) {
          countMap[tpaId] = (countMap[tpaId] || 0) + 1;
          if (!clientMap[tpaId]) clientMap[tpaId] = new Set();
          if (policy.client_id_ref && clientNameMap[policy.client_id_ref]) {
            clientMap[tpaId].add(clientNameMap[policy.client_id_ref]);
          }
        }
      }
      // rx_vendor_ids
      if (policy.rx_vendor_ids) {
        for (const rxId of policy.rx_vendor_ids) {
          countMap[rxId] = (countMap[rxId] || 0) + 1;
          if (!clientMap[rxId]) clientMap[rxId] = new Set();
          if (policy.client_id_ref && clientNameMap[policy.client_id_ref]) {
            clientMap[rxId].add(clientNameMap[policy.client_id_ref]);
          }
        }
      }
    }

    // Convert sets to arrays
    const clientArrayMap: Record<string, string[]> = {};
    for (const [vendorId, names] of Object.entries(clientMap)) {
      clientArrayMap[vendorId] = Array.from(names).sort();
    }

    return { policyCountMap: countMap, policyClientMap: clientArrayMap };
  }, [activePolicies, clientNameMap]);

  // Determine app location based on customResource
  const appLocation = (() => {
    switch (customResource) {
      case "vendors/carriers":
        return "vendors.carriers";
      case "vendors/tpas":
        return "vendors.tpas";
      case "vendors/rx":
        return "vendors.rx";
      default:
        return "vendors";
    }
  })();

  useDefineAppLocation(appLocation);

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        customTitle={getVendorTitle(customResource)}
        rightContent={<VendorsListActions loading={loading} />}
        headerIcon={<VendorsIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <List
        perPage={25}
        sort={{ field: "company_name", order: "ASC" }}
        resource={customResource || ResourceName.VENDORS}
        actions={false}
        aside={<VendorsListAside />}
        disableSyncWithLocation
        loading={loading}
        empty={<ListEmptyState resource={ResourceName.VENDORS} subtitle="No vendors found" />}
      >
        <StyledDatagridConfigurable
          rowClick={(id) => `/vendors/${id}/show`}
          omit={[
            "primary_contact_name",
            "primary_contact_email",
            "primary_contact_phone",
            "claims_contact_name",
            "claims_contact_email",
            "address",
            "naic_code",
            "fein",
            "website",
            "licensed_states",
          ]}
        >
          <TextField {...getProps("company_name", ResourceName.VENDORS)} />
          <FunctionField
            label="Vendor Type"
            sortable={false}
            render={(record: Vendor) => {
              const types: { label: string; color: StatusChipColor }[] = [];
              if (record.is_sl_carrier) types.push({ label: "Carrier", color: ColorType.Info });
              if (record.is_tpa) types.push({ label: "TPA", color: ColorType.Warning });
              if (record.is_rx_vendor) types.push({ label: "Rx", color: ColorType.Success });
              return types.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {types.map((t) => (
                    <StatusChip key={t.label} label={t.label} color={t.color} />
                  ))}
                </Box>
              ) : (
                "—"
              );
            }}
          />

          {/* Hidden-by-default columns */}
          <FunctionField
            source="active_policies"
            label="Active Policies"
            sortable={false}
            render={(record: Vendor) => (
              <ActivePoliciesCell vendor={record} policyCountMap={policyCountMap} policyClientMap={policyClientMap} />
            )}
          />
          <FunctionField
            source="primary_contact_name"
            label="Primary Contact"
            sortable={false}
            render={(record: Vendor) => (
              <Typography variant="body2" noWrap>
                {record.primary_contact?.name || "—"}
              </Typography>
            )}
          />
          <FunctionField
            source="primary_contact_email"
            label="Primary Email"
            sortable={false}
            render={(record: Vendor) =>
              record.primary_contact?.email ? (
                <Typography
                  variant="body2"
                  component={RouterLink}
                  to={`mailto:${record.primary_contact.email}`}
                  sx={{ color: "primary.main", textDecoration: "none" }}
                >
                  {record.primary_contact.email}
                </Typography>
              ) : (
                "—"
              )
            }
          />
          <FunctionField
            source="primary_contact_phone"
            label="Primary Phone"
            sortable={false}
            render={(record: Vendor) => <Typography variant="body2">{record.primary_contact?.phone || "—"}</Typography>}
          />
          <FunctionField
            source="claims_contact_name"
            label="Claims Contact"
            sortable={false}
            render={(record: Vendor) => (
              <Typography variant="body2" noWrap>
                {record.claims_contact?.name || "—"}
              </Typography>
            )}
          />
          <FunctionField
            source="claims_contact_email"
            label="Claims Email"
            sortable={false}
            render={(record: Vendor) =>
              record.claims_contact?.email ? (
                <Typography
                  variant="body2"
                  component={RouterLink}
                  to={`mailto:${record.claims_contact.email}`}
                  sx={{ color: "primary.main", textDecoration: "none" }}
                >
                  {record.claims_contact.email}
                </Typography>
              ) : (
                "—"
              )
            }
          />
          <FunctionField
            source="address"
            label="Location"
            sortable={false}
            render={(record: Vendor) => {
              const addr = record.address;
              if (!addr?.city && !addr?.state) return "—";
              return (
                <Typography variant="body2" noWrap>
                  {[addr?.city, addr?.state].filter(Boolean).join(", ")}
                </Typography>
              );
            }}
          />
          <TextField source="naic_code" label="NAIC Code" />
          <TextField source="fein" label="FEIN" />
          <FunctionField
            source="website"
            label="Website"
            render={(record: Vendor) =>
              record.website ? (
                <Typography
                  variant="body2"
                  component="a"
                  href={record.website.startsWith("http") ? record.website : `https://${record.website}`}
                  target="_blank"
                  rel="noopener"
                  sx={{ color: "primary.main", textDecoration: "none" }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  {record.website}
                </Typography>
              ) : (
                "—"
              )
            }
          />
          <FunctionField
            source="licensed_states"
            label="Licensed States"
            render={(record: Vendor) => {
              const states = record.licensed_states;
              if (!states || states.length === 0) return "—";
              if (states.length <= 3) return states.join(", ");
              return `${states.slice(0, 3).join(", ")} +${states.length - 3}`;
            }}
          />
        </StyledDatagridConfigurable>
      </List>
    </ListContextProvider>
  );
};

const VendorsListActions = ({ loading }: { loading?: boolean }) => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const { canCreate } = useResourcePermissions(ResourceName.VENDORS);

  return (
    <TopToolbar>
      {canCreate && (
        <CreateButton
          onClick={(e) => {
            e.preventDefault();
            setOpenCreateDialog(true);
          }}
          disabled={loading}
        />
      )}
      <SelectColumnsButton disabled={loading} />
      <ChunkedExportButton disabled={loading} />
      {isNonProductionEnvironment() && (
        <ExportButton
          label="Export JSON"
          icon={<DownloadIcon />}
          exporter={(records) => jsonExporter(records, ResourceName.VENDORS)}
          disabled={loading}
        />
      )}
      {canCreate && (
        <CreateInDialog<VendorCreate>
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          resource={ResourceName.VENDORS}
          transform={transformVendorCreate}
        />
      )}
    </TopToolbar>
  );
};
